import { useResolutionInfo } from '@mm/hooks';
import audiencesCRUDActions, {
  useAudiencesCRUDActions,
} from '@mm/state/actions/AudiencesCRUDActions';
import { selectInvalidAudienceContactRecordsIds } from '@mm/state/selectors/contactsSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { Pagination, StoreState } from '@mm/state/store-interfaces';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

export const useFetchAudienceContacts = (
  activeAudienceId?: number,
  options?: {
    pageSize?: number;
    skipBusy?: boolean;
    pauseRefreshingWhenEditingAudience?: boolean;
    withDeliverable?: boolean;
  },
) => {
  const { pageSize, skipBusy, withDeliverable } = options || {};
  const dispatch: Dispatch<StoreState> = useDispatch();
  const [isFetching, setIsFetching] = useState(false);

  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const invalidIds = useSelector(selectInvalidAudienceContactRecordsIds);
  const currentAudienceInvalid = invalidIds.includes(Number(activeAudienceId));
  const editingAudience = useSelector((state: StoreState) =>
    state.view.slideoutMenu.some(
      menu =>
        menu.menuType === 'editAudience' &&
        menu.menuProps?.audienceId === activeAudienceId,
    ),
  );
  const allowUpdateOfInvalidAudience =
    !options?.pauseRefreshingWhenEditingAudience || !editingAudience;

  const { getContactsById } = useAudiencesCRUDActions();
  const MOBILE_THRESHOLD = 1024;
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const fetch = useCallback(
    (options?: { ignoreErrors?: boolean }) => {
      const get = async () => {
        if (activeAudienceId) {
          setIsFetching(true);

          try {
            await getContactsById(
              activeChatbotId,
              activeAudienceId,
              activeFacebookPageId,
              {
                pageSize: pageSize ?? 25,
                skipBusy,
                throwErrors: options?.ignoreErrors,
                withDeliverable,
              },
            );
          } catch {
            // Ignore
          }

          setIsFetching(false);
        }
      };
      get();
    },
    [
      activeAudienceId,
      activeChatbotId,
      activeFacebookPageId,
      getContactsById,
      pageSize,
      skipBusy,
    ],
  );

  const mounted = useRef({});
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  });

  // fetch first time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fetch({ ignoreErrors: true }), [activeAudienceId]);

  // re-fetch on invalidation
  useEffect(() => {
    if (
      mounted.current &&
      activeAudienceId &&
      currentAudienceInvalid &&
      allowUpdateOfInvalidAudience &&
      !isFetching
    ) {
      fetch();
    }
  }, [
    activeAudienceId,
    allowUpdateOfInvalidAudience,
    currentAudienceInvalid,
    editingAudience,
    fetch,
    isFetching,
  ]);

  const fetchMore = async (pagination?: Pagination) => {
    if (pagination && !pagination.isLastPage && activeAudienceId && !isMobile) {
      setIsFetching(true);
      await getContactsById(
        activeChatbotId,
        activeAudienceId,
        activeFacebookPageId,
        {
          pageNumber: pagination.page + 1,
          pageSize: pagination.perPage,
          skipBusy,
          withDeliverable,
        },
      );
      setIsFetching(false);
    }
  };

  const fetchPage = async (
    pageSize?: number,
    pageNumber?: number,
    query?: string,
  ) => {
    if (pageSize && activeAudienceId) {
      setIsFetching(true);
      await getContactsById(
        activeChatbotId,
        activeAudienceId,
        activeFacebookPageId,
        {
          pageNumber,
          pageSize,
          query,
          skipBusy,
          showPageContacts: true,
          withDeliverable,
        },
      );
      setIsFetching(false);
    }
  };

  const invalidate = () => {
    dispatch({
      type: audiencesCRUDActions.updateAction,
      entity: { id: activeAudienceId },
    });
  };

  return {
    fetchMore,
    fetchPage,
    invalidate,
    isFetching,
  };
};
